import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../utils/axiosAuth";
import { IUserAuth } from "src/@types/auth";

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    name: string;
    phone: string;
    email: string;
    modules?: Array<string>;
    roles?: Array<string>;
    isSubUser?: boolean;
    ownerId?: string;
  };
}

interface RecoveryPassword {
  email: string;
}

interface NumberConfirm {
  success?: boolean;
  message?: string;
  userId?: string;
}

interface NewPassword {
  accessToken: string;
}

export const postLogin: any = createAsyncThunk<
  LoginResponse,
  { email: string; password: string }
>("auth/postLogin", async ({ email, password }) => {
  const response = await axios.post("/auth/login", {
    email,
    password,
  });
  return response.data;
});

export const postRegister: any = createAsyncThunk<
  LoginResponse,
  {
    userId: string;
    name: string;
    email: string;
    password: string;
    phone?: string;
    cpf?: string;
    city?: string;
    number?: string;
    street?: string;
    uf?: string;
    picture: string;
  }
>(
  "auth/postRegister",
  async ({
    userId,
    name,
    email,
    password,
    phone,
    cpf,
    city,
    number,
    street,
    uf,
    picture,
  }) => {
    const response = await axios.post("/auth/register/complete", {
      userId,
      name,
      email,
      password,
      phone,
      cpf,
      city,
      number,
      street,
      uf,
      picture,
    });
    return response.data;
  }
);

export const postNumberConfirm: any = createAsyncThunk<
  NumberConfirm,
  { phone: string }
>("auth/postNumberConfirm", async ({ phone }) => {
  const response = await axios.post("/auth/register/phone", { phone });
  return response.data;
});

export const postCodeConfirm: any = createAsyncThunk<
  NumberConfirm,
  { code: string; phone: string; purpose: string }
>("auth/postCodeConfirm", async ({ code, phone, purpose }) => {
  const response = await axios.post("/auth/verify", { code, phone, purpose });
  return response.data;
});

export const postRecoveryPassword: any = createAsyncThunk<
  RecoveryPassword,
  { phone: string }
>("auth/postRecoveryPassword", async ({ phone }) => {
  const response = await axios.post("/auth/recover/phone", { phone });
  return response.data;
});

export const postNewPassword: any = createAsyncThunk<
  RecoveryPassword,
  { hash: string; password: string }
>("auth/postNewPassword", async ({ hash, password }) => {
  const response = await axios.post("/auth/recover/reset", {
    hash,
    password,
  });
  return response.data;
});

export const postRefreshToken: any = createAsyncThunk<
  NewPassword,
  { refreshToken: string }
>("auth/postRefreshToken", async ({ refreshToken }) => {
  const response = await axios.post("/auth/refresh", {
    refreshToken: refreshToken,
  });
  return response.data;
});

const initialState: IUserAuth = {
  accessToken: "",
  refreshToken: "",
  userReturn: {
    id: "",
    name: "",
    email: "",
    phone: "",
    picture: "",
    modules: [],
    isSubUser: false,
    ownerId: "",
  },
  isLoading: false,
  error: undefined,
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    lockDashboardForAssistantConfiguration: (state) => {
      if (!state.userReturn.isSubUser) state.userReturn.modules = [];
    },
    logout: (state) => {
      state.accessToken = "";
      state.refreshToken = "";
      state.userReturn = initialState.userReturn;
      state.isLoading = false;
      state.error = "";
      state.messageRecovery = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // ExtraReducers para postLogin
      .addCase(postLogin.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(postLogin.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.userReturn = {
          ...state.userReturn,
          id: action.payload.user.id,
          name: action.payload.user.name,
          email: action.payload.user.email,
          phone: action.payload.user.phone,
          modules:
            action.payload.user.modules ?? action.payload.user.roles ?? [],
          isSubUser: !!action.payload.user.isSubUser,
          picture: action.payload.user?.picture || "",
        };
        state.error = undefined;
      })
      .addCase(postLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = "Error ao fazer login";
        state.accessToken = "";
        state.refreshToken = "";
        state.userReturn = initialState.userReturn;
      })
      .addCase(postRegister.pending, (state) => {
        state.isLoading = true;
        state.error = undefined;
      })
      .addCase(postRegister.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.userReturn = {
          ...state.userReturn,
          id: action.payload.user.id,
          name: action.payload.user.name,
          email: action.payload.user.email,
          phone: action.payload.user.phone,
          modules:
            action.payload.user.modules ?? action.payload.user.roles ?? [],
          isSubUser: !!action.payload.user.isSubUser,
          picture: action.payload.user?.picture || "",
          ownerId: action.payload.user?.ownerId || "",
        };
        state.error = undefined;
      })
      .addCase(postRegister.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
        state.accessToken = "";
        state.refreshToken = "";
        // state.userReturn = initialState.userReturn;
      })
      .addCase(postRecoveryPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postRecoveryPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(postRecoveryPassword.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(postNewPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postNewPassword.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(postNewPassword.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(postRefreshToken.pending, (state) => {
        state.isLoading = true;
        state.messageRecovery = "";
      })
      .addCase(postRefreshToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accessToken = action.payload.accessToken;
        state.refreshToken = action.payload.refreshToken;
        state.userReturn = {
          ...state.userReturn,
          id: action.payload.user.id,
          name: action.payload.user.name,
          email: action.payload.user.email,
          phone: action.payload.user.phone,
          modules:
            action.payload.user.modules ?? action.payload.user.roles ?? [],
          isSubUser: !!action.payload.user.isSubUser,
          picture: action.payload.user?.picture || "",
          ownerId: action.payload.user?.ownerId || "",
        };
      })
      .addCase(postRefreshToken.rejected, (state) => {
        state.accessToken = "";
        state.refreshToken = "";
        state.userReturn = initialState.userReturn;
        state.isLoading = false;
        state.error = "";
        state.messageRecovery = "";
      })
      .addCase(postNumberConfirm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(postNumberConfirm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;

        // Salvar o userId retornado da API
        state.userReturn.id = action.payload.userId;
      })
      .addCase(postNumberConfirm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Erro ao solicitar código.";
      })

      .addCase(postCodeConfirm.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(postCodeConfirm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.meta.arg.purpose === "register") {
          state.userReturn.id = action.payload.userId;
        }
      })
      .addCase(postCodeConfirm.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Código inválido ou expirado.";
      });
  },
});

export const { lockDashboardForAssistantConfiguration, logout } =
  authSlice.actions;

export default authSlice.reducer;
